"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sorterToCompare = exports.compareWithOrderedKeys = exports.getDefaultPlugins = exports.createCompare = exports.combine = exports.conditional = exports.Missing = void 0;
const isPlainObject_1 = __importDefault(require("lodash/isPlainObject"));
const isArray_1 = __importDefault(require("lodash/isArray"));
const isDate_1 = __importDefault(require("lodash/isDate"));
const isRegExp_1 = __importDefault(require("lodash/isRegExp"));
const sortBy_1 = __importDefault(require("lodash/sortBy"));
const reduce_1 = __importDefault(require("lodash/reduce"));
const map_1 = __importDefault(require("lodash/map"));
const toPairs_1 = __importDefault(require("lodash/toPairs"));
const forEach_1 = __importDefault(require("lodash/forEach"));
/**
 * @template [T=never]
 * @typedef { null | string | number | boolean | T | JSONObject<T> | JSONArray<T>} JSONValue
 */
/**
 * @template [T=never]
 * @typedef {{[x: string]: JSONValue<T>}} JSONObject
 */
/**
 * @template [T=never]
 * @typedef {JSONValue<T>[]} JSONArray
 */
class Missing {
}
exports.Missing = Missing;
// NOTE: Because NaN < NaN => false, this function returns 0 for (NaN, NaN)
/**
 * @template T
 * @param {T} x
 * @param {T} y
 * @returns
 */
function defaultCompare(x, y) {
    if (x < y) {
        return -1;
    }
    if (x > y) {
        return 1;
    }
    return 0;
}
/**
 * @template T
 * @typedef {(x: unknown) => x is T} Predicate
 */
/**
 * @template T
 * @typedef {(x: T, y: T) => number} CompareFunction
 */
/**
 * @template T
 * @template {T} [U=T]
 * @param {Predicate<U>} predicate
 * @param {CompareFunction<U>} specialCase
 * @returns {CompareFunction<T>}
 */
function conditional(predicate, specialCase) {
    return (x, y) => {
        if (predicate(x)) {
            if (!predicate(y)) {
                return -1;
            }
            return specialCase(x, y);
        }
        if (predicate(y)) {
            return 1;
        }
        return 0;
    };
}
exports.conditional = conditional;
/**
 * @template T
 * @param {T} x
 * @returns {() => T}
 */
const constant = (x) => () => x;
/**
 * @template T
 * @param {CompareFunction<T>[]} comparators
 * @returns {CompareFunction<T>}
 */
const combine = (comparators) => {
    return (0, reduce_1.default)(comparators, (previousCompare, currentCompare) => (x, y) => {
        const result = previousCompare(x, y);
        if (result !== 0) {
            return result;
        }
        return currentCompare(x, y);
    }, constant(0));
};
exports.combine = combine;
/**
 * @param {unknown} x
 * @returns {x is JSONObject}
 */
function isJSONObject(x) {
    return (0, isPlainObject_1.default)(x);
}
/**
 * @template T
 * @param {CompareFunction<T>} compare
 * @returns {CompareFunction<T | T[]>}
 */
function pluginArray(compare) {
    return conditional(isArray_1.default, 
    /**
     * @param {T[]} x
     * @param {T[]} y
     * @returns {number}
     */
    (x, y) => {
        const n = x.length;
        const m = y.length;
        for (let i = 0; i < n && i < m; i += 1) {
            const result = compare(x[i], y[i]);
            if (result !== 0) {
                return result;
            }
        }
        return defaultCompare(n, m);
    });
}
/**
 * @template [T=never]
 * @typedef {JSONValue<Missing | Date | RegExp | undefined | T>} Comparable
 */
/**
 * @param {CompareFunction<Comparable>} compare
 * @param {PluginOptions} [options]
 * @returns {CompareFunction<Comparable>}
 */
function pluginObject(compare, options = {}) {
    /**
     * @type {(x: JSONObject, y: JSONObject) => number}
     */
    const compareObjects = options && options.orderedKeys
        ? (x, y) => compare((0, toPairs_1.default)(x), (0, toPairs_1.default)(y))
        : (x, y) => compare((0, sortBy_1.default)((0, toPairs_1.default)(x), '0'), (0, sortBy_1.default)((0, toPairs_1.default)(y), '0'));
    return conditional(isJSONObject, compareObjects);
}
const createTypePredicate = 
/**
 * @type {{
 * (type: 'string'): (x: unknown) => x is string;
 * (type: 'number'): (x: unknown) => x is number;
 * (type: 'boolean'): (x: unknown) => x is boolean;
 * }}
 */
(
/**
 * @template T
 * @param {string} type
 * @returns {(x: unknown) => x is T}
 */
(type) => {
    /**
     * @param {unknown} x
     * @returns {x is T}
     */
    function isOfType(x) {
        // eslint-disable-next-line valid-typeof
        return typeof x === type;
    }
    return isOfType;
});
/**
 * @template T
 * @param {T} literal
 * @returns {() => CompareFunction<unknown>}
 */
const createLiteral = (literal) => {
    /**
     * @param {unknown} x
     * @returns {x is T}
     */
    function isTheSame(x) {
        return x === literal;
    }
    return () => conditional(isTheSame, defaultCompare);
};
/**
 * @typedef {object} PluginOptions
 * @property {boolean} [orderedKeys]
 */
/**
 * @template T
 * @typedef {(compare: CompareFunction<T>, options: PluginOptions) => CompareFunction<T>} Plugin
 */
/**
 * @type {Plugin<Comparable>}
 */
const pluginDate = () => conditional(isDate_1.default, defaultCompare);
/**
 * @type {Plugin<Comparable>}
 */
const pluginRegExp = () => conditional(isRegExp_1.default, defaultCompare);
/**
 * @template T
 * @param {readonly Plugin<T>[]} plugins
 * @param {PluginOptions} [options]
 * @returns {CompareFunction<T>}
 */
function createCompare(plugins, options = {}) {
    /**
     * @type {CompareFunction<T>}
     */
    let compiled;
    /**
     * @type {CompareFunction<T>}
     */
    const compare = (x, y) => compiled(x, y);
    compiled = (0, exports.combine)((0, map_1.default)(plugins, (plugin) => plugin(compare, options)));
    return compare;
}
exports.createCompare = createCompare;
/**
 * @param {unknown} value
 * @returns {value is Missing | undefined}
 */
function isMissing(value) {
    return value instanceof Missing || typeof value === 'undefined';
}
// See:
// https://docs.mongodb.com/manual/reference/bson-type-comparison-order/
/**
 * @returns {Plugin<Comparable>[]}
 */
const getDefaultPlugins = () => [
    () => conditional(isMissing, constant(0)),
    createLiteral(null),
    () => conditional(createTypePredicate('number'), defaultCompare),
    () => conditional(createTypePredicate('string'), defaultCompare),
    pluginObject,
    pluginArray,
    () => conditional(createTypePredicate('boolean'), defaultCompare),
    pluginDate,
    pluginRegExp,
];
exports.getDefaultPlugins = getDefaultPlugins;
const plugins = (0, exports.getDefaultPlugins)();
const compare = createCompare(plugins);
exports.compareWithOrderedKeys = createCompare(plugins, {
    orderedKeys: true,
});
/**
 * @template {Record<string, Comparable>} T
 * @param {Record<keyof T, number>} sorter
 * @returns {(a: T, b: T) => number}
 */
const sorterToCompare = (sorter) => {
    /** @type {(keyof T)[]} */
    const fields = [];
    /** @type {number[]} */
    const orders = [];
    (0, forEach_1.default)(sorter, (order, field) => {
        fields.push(/** @type {keyof T} */ (field));
        orders.push(order);
    });
    const n = fields.length;
    return (a, b) => {
        for (let i = 0; i < n; i += 1) {
            const field = fields[i];
            const order = orders[i];
            const value = compare(a[field], b[field]);
            if (value < 0) {
                return -order;
            }
            if (value > 0) {
                return order;
            }
        }
        return 0;
    };
};
exports.sorterToCompare = sorterToCompare;
exports.default = compare;
//# sourceMappingURL=compare.js.map